import { createReducer, combineReducersWithRoot } from '@common/redux-utils';
import {
  InvoiceActionType,
  SearchInvoiceListStartAction,
  SearchInvoiceListSuccessAction,
  SearchInvoiceListFailureAction,
  GetInvoiceStartAction,
  GetInvoiceSuccessAction,
  GetInvoiceFailureAction,
  OwnershipChangeStartAction,
  OwnershipChangeSuccessAction,
  SetInvoiceableSwoCVIsAction,
  SetInvoicePayeesAction,
  SetInvoicePayeesListLoadingAction,
  FakeSave,
  SaveInvoiceFilesStartAction,
  SaveInvoiceFilesSuccessAction,
  SaveInvoiceFilesFailureAction,
  RemoveInvoiceFileStartAction,
  RemoveInvoiceFileSuccessAction,
  RemoveInvoiceFileFailureAction,
  GetInvoiceFilesStartAction,
  GetInvoiceFilesSuccessAction,
  GetInvoiceFilesFailureAction,
  CreateInvoiceStartAction,
  CreateInvoiceSuccessAction,
  InvoiceSavingAction
} from './invoicing.actions';
import { InvoiceViewModel } from '@models/Invoice';
import { OwnershipChangeFailureAction } from './invoicing.actions';
import { InvoicePaymentItemListReducer, InvoicePaymentLineState } from './invoice-line.reducer';
import { InvoiceSearchViewModel } from '../_models/Invoice';
import { SwoCviViewModel } from '@models/AuthorizationSwoCvi';
import { InvoicePayeesViewModel, InvoicePayeeViewModel } from '@models/InvoicePayees';
import { DocumentViewModel } from '@models/Document';
import { SaveCustomerFailureAction } from 'src/customer/customer.actions';

export interface InvoicingState {
  readonly invoiceList: Array<any>;
  readonly query: string | null;
  readonly filterBy: string | null;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly pageCount: number;
  readonly orderBy: string;
  readonly message: string;
  readonly loading?: boolean;
  readonly invoice?: InvoiceViewModel;
  readonly invoicableCvis: Array<SwoCviViewModel>;
  readonly paymentLines: InvoicePaymentLineState;
  readonly invoicePayees?: InvoicePayeesViewModel;
  readonly selectedPayee?: InvoicePayeeViewModel;
  readonly payeesLoading: boolean;
  readonly invoiceSaving: boolean;
  readonly documents: DocumentViewModel[];
}

export const initialInvoicingState: InvoicingState = {
  invoiceList: [] as Array<any>,
  query: '',
  filterBy: '',
  pageNumber: 0,
  pageSize: 10,
  pageCount: 10,
  orderBy: '',
  message: '',
  invoice: undefined,
  paymentLines: {} as InvoicePaymentLineState,
  invoicableCvis: [],
  invoicePayees: undefined,
  selectedPayee: undefined,
  payeesLoading: false,
  invoiceSaving: false,
  documents: []
};

/********** Save **********/
function handleCreateInvoiceStart(state: InvoicingState, action: CreateInvoiceStartAction): InvoicingState {
  return {
    ...state
  };
}
function handleCreateInvoiceSuccess(state: InvoicingState, action: CreateInvoiceSuccessAction): InvoicingState {
  if (!action.invoice.id) {
    return state;
  }
  return {
    ...state,
    invoice: action.invoice
  };
}
function handleCreateInvoiceFailure(state: InvoicingState, action: SaveCustomerFailureAction): InvoicingState {
  if (!action.errorMessage) {
    return {
      ...state
    };
  }
  return {
    ...state
  };
}

/***** Search Dispatch List ****/
function handleSearchInvoiceListStart(state: InvoicingState, action: SearchInvoiceListStartAction): InvoicingState {
  return {
    ...state,
    query: action.query,
    filterBy: action.filterBy,
    loading: true
  };
}

function handleSearchInvoiceListSuccess(state: InvoicingState, action: SearchInvoiceListSuccessAction): InvoicingState {
  return {
    ...state,
    pageNumber: action.pageNumber,
    pageSize: action.pageSize,
    pageCount: Math.ceil(action.result.totalResults / action.result.pageSize),
    invoiceList: action.result.results,
    orderBy: action.orderBy,
    loading: false
  };
}
function handleSearchInvoiceListFailure(state: InvoicingState, action: SearchInvoiceListFailureAction): InvoicingState {
  return {
    ...state,
    message: action.error,
    loading: false
  };
}

function handleGetInvoiceStartAction(state: InvoicingState, action: GetInvoiceStartAction): InvoicingState {
  return {
    ...state,
    message: action.type
  };
}

function handleGetInvoiceSuccessAction(state: InvoicingState, action: GetInvoiceSuccessAction): InvoicingState {
  return {
    ...state,
    invoice: action.invoice,
    message: action.type
  };
}

function handleGetInvoiceFailureAction(state: InvoicingState, action: GetInvoiceFailureAction): InvoicingState {
  return {
    ...state,
    message: action.errorMessage
  };
}

function handleOwnershipChangeStart(state: InvoicingState, action: OwnershipChangeStartAction): InvoicingState {
  return {
    ...state,
    loading: true
  };
}

function handleOwnershipChangeSuccess(state: InvoicingState, action: OwnershipChangeSuccessAction): InvoicingState {
  let newList = [] as Array<InvoiceSearchViewModel>;
  if (state.invoiceList) {
    let list: Array<InvoiceSearchViewModel> = state.invoiceList;
    let result: InvoiceSearchViewModel | undefined = list.find(x => {
      return x.id === action.id;
    });
    if (result) {
      const index = list.indexOf(result);
      if (index !== -1) {
        list[index] = action.release
          ? { ...result, owner: undefined }
          : { ...result, owner: { id: action.user.id, name: action.user.firstName + ' ' + action.user.lastName } };
        // manually sets the owner after ownership change, since ES doesn't update fast enough
      }
      newList = list;
    }
  }

  return {
    ...state,
    loading: false,
    invoiceList: newList
  };
}

function handleOwnershipChangeFailure(state: InvoicingState, action: OwnershipChangeFailureAction): InvoicingState {
  return {
    ...state,
    message: action.error,
    loading: false
  };
}

function handleSetInvoicableCvis(state: InvoicingState, action: SetInvoiceableSwoCVIsAction): InvoicingState {
  return {
    ...state,
    invoicableCvis: action.cvis
  };
}

function handleSetInvoicePayees(state: InvoicingState, action: SetInvoicePayeesAction): InvoicingState {
  return {
    ...state,
    invoicePayees: action.invoicePayees
  };
}

function handleSetInvoicePayeesListLoading(
  state: InvoicingState,
  action: SetInvoicePayeesListLoadingAction
): InvoicingState {
  return {
    ...state,
    payeesLoading: action.payeesLoading
  };
}

function handleInvoiceSaving(state: InvoicingState, action: InvoiceSavingAction): InvoicingState {
  return {
    ...state,
    invoiceSaving: action.invoiceSaving
  };
}

function handleFakeSave(state: InvoicingState, action: FakeSave): InvoicingState {
  return {
    ...state
  };
}

function handleInvoiceReset(state: InvoicingState, action: InvoiceSavingAction): InvoicingState {
  return initialInvoicingState;
}

/********** SaveInvoiceFiles **********/
function handleSaveInvoiceFilesStart(state: InvoicingState, action: SaveInvoiceFilesStartAction): InvoicingState {
  return state;
}
function handleSaveInvoiceFilesSuccess(state: InvoicingState, action: SaveInvoiceFilesSuccessAction): InvoicingState {
  if (!action.documents) {
    return state;
  }
  return {
    ...state,
    message: 'SUCCESS - contract files saved - ' + action.invoiceId
  };
}
function handleSaveInvoiceFilesFailure(state: InvoicingState, action: SaveInvoiceFilesFailureAction): InvoicingState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    message: 'ERROR - save contract files failed - ' + action.errorMessage
  };
}

/********** RemoveInvoiceFile **********/
function handleRemoveInvoiceFileStart(state: InvoicingState, action: RemoveInvoiceFileStartAction): InvoicingState {
  return state;
}
function handleRemoveInvoiceFileSuccess(state: InvoicingState, action: RemoveInvoiceFileSuccessAction): InvoicingState {
  if (!action.fileId) {
    return state;
  }
  return {
    ...state,
    message: 'SUCCESS - invoice file removed - ' + action.invoiceId
  };
}
function handleRemoveInvoiceFileFailure(state: InvoicingState, action: RemoveInvoiceFileFailureAction): InvoicingState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    message: 'ERROR - remove invoice file failed - ' + action.errorMessage
  };
}

/********** GetInvoiceFiles **********/
function handleGetInvoiceFilesStart(state: InvoicingState, action: GetInvoiceFilesStartAction): InvoicingState {
  return state;
}
function handleGetInvoiceFilesSuccess(state: InvoicingState, action: GetInvoiceFilesSuccessAction): InvoicingState {
  if (!action.invoiceId) {
    return state;
  }
  return {
    ...state,
    message: 'SUCCESS - get invoice files done - ' + action.invoiceId,
    documents: action.documents
  };
}
function handleGetInvoiceFilesFailure(state: InvoicingState, action: GetInvoiceFilesFailureAction): InvoicingState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    message: 'ERROR - get invoice files failed - ' + action.errorMessage
  };
}

const InvoicingParentReducer = createReducer<InvoicingState, InvoiceActionType>(initialInvoicingState, {
  [InvoiceActionType.CreateInvoiceStart]: handleCreateInvoiceStart,
  [InvoiceActionType.CreateInvoiceSuccess]: handleCreateInvoiceSuccess,
  [InvoiceActionType.CreateInvoiceFailure]: handleCreateInvoiceFailure,
  [InvoiceActionType.SearchInvoiceListStart]: handleSearchInvoiceListStart,
  [InvoiceActionType.SearchInvoiceListSuccess]: handleSearchInvoiceListSuccess,
  [InvoiceActionType.SearchInvoiceListFailure]: handleSearchInvoiceListFailure,

  [InvoiceActionType.GetInvoiceStart]: handleGetInvoiceStartAction,
  [InvoiceActionType.GetInvoiceSuccess]: handleGetInvoiceSuccessAction,
  [InvoiceActionType.GetInvoiceFailure]: handleGetInvoiceFailureAction,

  [InvoiceActionType.OwnershipChangeStart]: handleOwnershipChangeStart,
  [InvoiceActionType.OwnershipChangeSuccess]: handleOwnershipChangeSuccess,
  [InvoiceActionType.OwnershipChangeFailure]: handleOwnershipChangeFailure,
  [InvoiceActionType.SetInvoicableSwoCVIs]: handleSetInvoicableCvis,

  [InvoiceActionType.SetInvoicePayees]: handleSetInvoicePayees,
  [InvoiceActionType.SetInvoicePayeesListLoading]: handleSetInvoicePayeesListLoading,

  [InvoiceActionType.InvoiceSaving]: handleInvoiceSaving,
  [InvoiceActionType.FakeSave]: handleFakeSave,
  [InvoiceActionType.InvoiceReset]: handleInvoiceReset,
  [InvoiceActionType.SaveInvoiceFilesStart]: handleSaveInvoiceFilesStart,
  [InvoiceActionType.SaveInvoiceFilesSuccess]: handleSaveInvoiceFilesSuccess,
  [InvoiceActionType.SaveInvoiceFilesFailure]: handleSaveInvoiceFilesFailure,
  [InvoiceActionType.RemoveInvoiceFileStart]: handleRemoveInvoiceFileStart,
  [InvoiceActionType.RemoveInvoiceFileSuccess]: handleRemoveInvoiceFileSuccess,
  [InvoiceActionType.RemoveInvoiceFileFailure]: handleRemoveInvoiceFileFailure,
  [InvoiceActionType.GetInvoiceFilesStart]: handleGetInvoiceFilesStart,
  [InvoiceActionType.GetInvoiceFilesSuccess]: handleGetInvoiceFilesSuccess,
  [InvoiceActionType.GetInvoiceFilesFailure]: handleGetInvoiceFilesFailure
});

export const InvoicingReducer = combineReducersWithRoot(InvoicingParentReducer, {
  paymentLines: InvoicePaymentItemListReducer
});



// WEBPACK FOOTER //
// ./src/invoicing/invoicing.reducer.ts